import { EQUITY_INDUSTRY_GROUPS } from './facetOptions';
import {
  CountBubble,
  FacetApplyPopover,
  FacetCheckboxButton,
  useSyncedPendingFacetState,
} from './searchFacetFilterPrimitives';
import {
  ALL_INDUSTRY_SLUGS,
  ALL_SECTOR_SLUGS,
  getAppliedEquitySectorSelection,
  getEffectiveEquitySectorSelection,
  getEquitySectorSelectedCount,
  getEquitySectorTriggerLabel,
  getFilteredSectorGroups,
  getSectorGroupCheckedState,
  toggleIndustrySelection,
  toggleSectorGroupSelection,
} from './searchFacetFilterUtils';

function EquitySectorTriggerContent({
  selectedSectorSlugs,
  selectedIndustrySlugs,
}: {
  selectedSectorSlugs: string[];
  selectedIndustrySlugs: string[];
}) {
  const selectedCount = getEquitySectorSelectedCount({
    selectedSectorSlugs,
    selectedIndustrySlugs,
  });

  return (
    <>
      <span className="min-w-0 truncate">Sector & Industry</span>
      {selectedCount > 0 ? <CountBubble count={selectedCount} /> : null}
    </>
  );
}

export function EquitySectorFacetMenu({
  selectedSectorSlugs,
  selectedIndustrySlugs,
  triggerClassName,
  popupClassName,
  popupMatchTrigger,
  onApply,
}: {
  selectedSectorSlugs: string[];
  selectedIndustrySlugs: string[];
  triggerClassName?: string;
  popupClassName?: string;
  popupMatchTrigger?: boolean;
  onApply: (next: {
    equity_sector_slug?: string[];
    equity_industry_slug?: string[];
  }) => void;
}) {
  const effectiveSelection = getEffectiveEquitySectorSelection(
    selectedSectorSlugs,
    selectedIndustrySlugs,
  );
  const selectionKey = `${selectedSectorSlugs.join('|')}::${selectedIndustrySlugs.join('|')}`;
  const [pendingSectorSlugs, setPendingSectorSlugs] =
    useSyncedPendingFacetState(effectiveSelection.sectorSlugs, selectionKey);
  const [pendingIndustrySlugs, setPendingIndustrySlugs] =
    useSyncedPendingFacetState(effectiveSelection.industrySlugs, selectionKey);
  const pendingSectorValues = new Set(pendingSectorSlugs);
  const pendingIndustryValues = new Set(pendingIndustrySlugs);
  const resetPendingSelection = () => {
    setPendingSectorSlugs(effectiveSelection.sectorSlugs);
    setPendingIndustrySlugs(effectiveSelection.industrySlugs);
  };

  return (
    <FacetApplyPopover
      triggerClassName={triggerClassName}
      triggerTrackProps={{
        'data-track': 'search_facet_open',
        'data-track-label': 'Sector',
        'data-track-context': 'search_facet',
      }}
      popupClassName={popupClassName}
      popupMatchTrigger={popupMatchTrigger}
      label={getEquitySectorTriggerLabel({
        selectedSectorSlugs,
        selectedIndustrySlugs,
      })}
      triggerContent={
        <EquitySectorTriggerContent
          selectedSectorSlugs={selectedSectorSlugs}
          selectedIndustrySlugs={selectedIndustrySlugs}
        />
      }
      searchPlaceholder="Search sectors or industries"
      onClear={() => {
        setPendingSectorSlugs([]);
        setPendingIndustrySlugs([]);
      }}
      onClose={resetPendingSelection}
      onApply={() => {
        onApply(
          getAppliedEquitySectorSelection(
            pendingSectorSlugs,
            pendingIndustrySlugs,
          ),
        );
      }}
      onSelectAll={() => {
        setPendingSectorSlugs(ALL_SECTOR_SLUGS);
        setPendingIndustrySlugs(ALL_INDUSTRY_SLUGS);
      }}
      applyDisabled={
        pendingSectorSlugs.length === 0 && pendingIndustrySlugs.length === 0
      }
    >
      {({ searchQuery }) => {
        const filteredGroups = getFilteredSectorGroups(
          EQUITY_INDUSTRY_GROUPS,
          searchQuery,
        );

        return filteredGroups.map((group) => {
          const groupState = getSectorGroupCheckedState(
            group,
            pendingSectorValues,
            pendingIndustryValues,
          );

          return (
            <div key={group.value}>
              <FacetCheckboxButton
                label={group.label}
                checked={groupState.checked}
                indeterminate={groupState.indeterminate}
                emphasized
                onClick={() => {
                  const next = toggleSectorGroupSelection({
                    group,
                    selectedSectorSlugs: pendingSectorSlugs,
                    selectedIndustrySlugs: pendingIndustrySlugs,
                  });
                  setPendingSectorSlugs(next.sectorSlugs);
                  setPendingIndustrySlugs(next.industrySlugs);
                }}
              />

              <div>
                {group.industries.map((industry) => (
                  <FacetCheckboxButton
                    key={industry.value}
                    label={industry.label}
                    checked={pendingIndustryValues.has(industry.value)}
                    nested
                    onClick={() => {
                      const next = toggleIndustrySelection({
                        group,
                        industrySlug: industry.value,
                        selectedSectorSlugs: pendingSectorSlugs,
                        selectedIndustrySlugs: pendingIndustrySlugs,
                      });
                      setPendingSectorSlugs(next.sectorSlugs);
                      setPendingIndustrySlugs(next.industrySlugs);
                    }}
                  />
                ))}
              </div>
            </div>
          );
        });
      }}
    </FacetApplyPopover>
  );
}
